import { usePaidTraining } from "../training-payments/TrainingAccess";
import { trainingRequest } from "../training-payments/api";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { Chess } from "chess.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faBolt,
  faChessKnight,
  faCrown,
  faLink,
  faMicrophone,
  faPaperPlane,
  faRotateLeft,
  faVolumeHigh,
} from "@fortawesome/free-solid-svg-icons";

import HoodEngineBoardWeb from "../components/HoodEngineBoardWeb";
import { requestHoodCoach } from "../hood-training/hoodTransport";
import HoodBeginnerTraining from "./HoodBeginnerTraining";
import HoodFirstTimeOnboarding from "./HoodFirstTimeOnboarding";
import { lessons as beginnerLessons } from "../hood-training/trainingModel";
import ResponsiveAppMenu from "../components/ResponsiveAppMenu";
import HoodVoiceControls from "../components/HoodVoiceControls";
import HoodLevelPicker from "../components/HoodLevelPicker";
import HoodCareerPath from "../components/HoodCareerPath";
import { auth } from "../components/firebase";
import { getFirstPuzzle } from "../integrations/APIClient";
import { UserContext } from "../contexts/UserContext";
import { useLanguage } from "../contexts/LanguageContext";
import { usePro } from "../contexts/ProContext";
import { useOwnerQa } from "../contexts/OwnerQaContext";
import { getWebHoodVoiceEnabled, speakWebHoodVoice } from "../utils/hoodWebSpeech";
import { trackUsageEvent } from "../analytics/UsageAnalytics";

const START_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";
const OPENING_EXPLORER = "https://explorer.lichess.ovh";
const GOLD = "#FFD400";
const BG = "#061A34";
const CARD = "rgba(8, 13, 28, 0.94)";
const BORDER = "rgba(255, 212, 0, 0.30)";
const TEXT = "#F8FAFC";
const MUTED = "#AFC2D8";

const LEVELS = [
  { elo: 800, en: "Starter", pl: "Początek", es: "Inicio" },
  { elo: 1000, en: "Hut", pl: "Domek", es: "Cabaña" },
  { elo: 1200, en: "Squire", pl: "Giermek", es: "Escudero" },
  { elo: 1400, en: "Knight", pl: "Rycerz", es: "Caballero" },
  { elo: 1600, en: "Guardian", pl: "Strażnik", es: "Guardián" },
  { elo: 1800, en: "Tower", pl: "Wieża", es: "Torre" },
  { elo: 2000, en: "Castle", pl: "Zamek", es: "Castillo" },
  { elo: 2200, en: "Dragon", pl: "Smok", es: "Dragón" },
  { elo: 2400, en: "Master", pl: "Mistrz", es: "Maestro" },
  { elo: 2600, en: "Legend", pl: "Legenda", es: "Leyenda" },
  { elo: 2800, en: "Grandmaster", pl: "Arcymistrz", es: "Gran Maestro" },
  { elo: 3000, en: "Super GM", pl: "Super GM", es: "Super GM" },
];

const COPY = {
  coach: { en: "HOOD AI COACH", pl: "TRENER HOOD AI", es: "ENTRENADOR HOOD AI" },
  decides: {
    en: "Connect Lichess or play the first move — Hood will coach you step by step",
    pl: "Połącz Lichess albo zagraj pierwszy ruch — Hood poprowadzi Cię krok po kroku",
    es: "Conecta Lichess o juega la primera jugada — Hood te guiará paso a paso",
  },
  rating: { en: "YOUR RATING", pl: "TWÓJ RANKING", es: "TU RATING" },
  plan: { en: "TODAY'S PLAN", pl: "PLAN NA DZIŚ", es: "PLAN DE HOY" },
  connect: { en: "Connect Lichess", pl: "Połącz Lichess", es: "Conectar Lichess" },
  connectButton: { en: "Connect & analyse", pl: "Połącz i przeanalizuj", es: "Conectar y analizar" },
  username: { en: "Lichess username", pl: "nick z Lichess", es: "usuario de Lichess" },
  connected: { en: "connected", pl: "połączony", es: "conectado" },
  recent10: { en: "Last 10 games", pl: "Ostatnie 10 partii", es: "Últimas 10 partidas" },
  weakest: { en: "Weakest opening", pl: "Najsłabsze otwarcie", es: "Apertura más débil" },
  tournament: { en: "Last tournament", pl: "Ostatni turniej", es: "Último torneo" },
  noTournament: { en: "No recent tournament found", pl: "Nie znaleziono ostatniego turnieju", es: "No se encontró un torneo reciente" },
  ask: {
    en: "Ask Hood or say what you want to practise…",
    pl: "Zapytaj Hooda albo powiedz, co chcesz ćwiczyć…",
    es: "Pregunta a Hood o di qué quieres practicar…",
  },
  listening: { en: "Listening…", pl: "Słucham…", es: "Escuchando…" },
  thinking: { en: "Hood is thinking…", pl: "Hood myśli…", es: "Hood está pensando…" },
  engine: { en: "Stockfish", pl: "Stockfish", es: "Stockfish" },
  pro: { en: "PRO", pl: "PRO", es: "PRO" },
  free: { en: "FREE", pl: "FREE", es: "GRATIS" },
  upgrade: { en: "UNLOCK CHESSHOOD PRO", pl: "ODBLOKUJ CHESSHOOD PRO", es: "DESBLOQUEAR CHESSHOOD PRO" },
  accessTitle: { en: "Hood AI Pro", pl: "Hood AI Pro", es: "Hood AI Pro" },
  puzzle: { en: "Personal puzzle", pl: "Puzzle dla Ciebie", es: "Puzzle para ti" },
  position: { en: "Position challenge", pl: "Pozycja do analizy", es: "Posición para analizar" },
  drill: { en: "Opening drill", pl: "Trening otwarcia", es: "Práctica de apertura" },
  freeGame: { en: "Full game vs Stockfish", pl: "Pełna partia ze Stockfishem", es: "Partida completa contra Stockfish" },
  you: { en: "You", pl: "Ty", es: "Tú" },
  hood: { en: "Hood", pl: "Hood", es: "Hood" },
};

const ACTION_RE = /\[\[HOOD_ACTION:(ASK|PUZZLE|POSITION|OPENING_DRILL)\]\]/i;

function tr(language, value) {
  return value?.[language] || value?.en || "";
}

function makeStorageId(prefix, uid) {
  return `${prefix}:${uid || "guest"}`;
}

function sessionIdFor(uid) {
  const key = makeStorageId("hoodTrainerSessionId:web:v1", uid);
  try {
    const saved = window.localStorage.getItem(key);
    if (saved) return saved;
    const next = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `hood-web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(key, next);
    return next;
  } catch {
    return `hood-web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function parseHoodSpeech(value) {
  const raw = String(value || "").trim();
  const match = raw.match(ACTION_RE);
  return {
    action: match ? String(match[1] || "").toUpperCase() : "",
    speech: raw.replace(ACTION_RE, "").replace(/\s+/g, " ").trim(),
  };
}

function moveVerdict(cpLoss) {
  const loss = Number(cpLoss);
  if (!Number.isFinite(loss)) return "unknown";
  if (loss >= 200) return "blunder";
  if (loss >= 100) return "mistake";
  if (loss >= 55) return "inaccuracy";
  if (loss <= 20) return "excellent";
  return "good";
}

function humanEngineAssessment(cpValue, mateValue, language = "en") {
  const mate = Number(mateValue);
  if (Number.isFinite(mate)) {
    if (language === "pl") return mate > 0 ? "Masz wymuszoną drogę do mata." : "Trzeba uważać — przeciwnik ma groźbę wymuszonego mata.";
    if (language === "es") return mate > 0 ? "Tienes una secuencia forzada de mate." : "Cuidado: el rival tiene una amenaza de mate forzado.";
    return mate > 0 ? "You have a forced mating sequence." : "Be careful — the opponent has a forced mating threat.";
  }
  const cp = Number(cpValue);
  if (!Number.isFinite(cp)) return "";
  const a = Math.abs(cp);
  if (language === "pl") {
    if (a <= 25) return "Pozycja jest mniej więcej równa.";
    if (cp > 0 && a <= 80) return "Masz niewielką przewagę.";
    if (cp > 0 && a <= 160) return "Masz wyraźną przewagę.";
    if (cp > 0) return "Masz dużą przewagę.";
    if (a <= 80) return "Przeciwnik ma niewielką przewagę.";
    if (a <= 160) return "Przeciwnik ma wyraźną przewagę.";
    return "Przeciwnik ma dużą przewagę.";
  }
  if (language === "es") {
    if (a <= 25) return "La posición está aproximadamente igualada.";
    if (cp > 0 && a <= 80) return "Tienes una pequeña ventaja.";
    if (cp > 0 && a <= 160) return "Tienes una ventaja clara.";
    if (cp > 0) return "Tienes una gran ventaja.";
    if (a <= 80) return "El rival tiene una pequeña ventaja.";
    if (a <= 160) return "El rival tiene una ventaja clara.";
    return "El rival tiene una gran ventaja.";
  }
  if (a <= 25) return "The position is roughly equal.";
  if (cp > 0 && a <= 80) return "You have a small edge.";
  if (cp > 0 && a <= 160) return "You have a clear advantage.";
  if (cp > 0) return "You have a strong advantage.";
  if (a <= 80) return "Your opponent has a small edge.";
  if (a <= 160) return "Your opponent has a clear advantage.";
  return "Your opponent has a strong advantage.";
}

function parseNdjson(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      try { return JSON.parse(line); } catch { return null; }
    })
    .filter(Boolean);
}

function lichessPlayerName(player) {
  return String(player?.userId || player?.name || player?.user?.id || player?.user?.name || player?.username || "").trim();
}

function lichessGameSide(game, username) {
  const target = String(username || "").trim().toLowerCase();
  const white = lichessPlayerName(game?.players?.white).toLowerCase();
  const black = lichessPlayerName(game?.players?.black).toLowerCase();
  if (target && white === target) return "white";
  if (target && black === target) return "black";
  return null;
}

function gameOutcome(game, side) {
  const winner = String(game?.winner || "").trim().toLowerCase();
  if (!winner) return "draw";
  return winner === side ? "win" : "loss";
}

function gameMoves(game) {
  if (Array.isArray(game?.moves)) return game.moves.map((move) => String(move || "").trim()).filter(Boolean);
  return String(game?.moves || "").trim().split(/\s+/).filter(Boolean);
}

function openingName(game) {
  return String(game?.opening?.name || game?.opening?.eco || game?.eco || "").trim();
}

function recentTen(games, username) {
  const recent = [...(Array.isArray(games) ? games : [])]
    .sort((a, b) => Number(b?.createdAt || b?.lastMoveAt || 0) - Number(a?.createdAt || a?.lastMoveAt || 0))
    .slice(0, 10);
  const result = { games: recent.length, wins: 0, draws: 0, losses: 0 };
  recent.forEach((game) => {
    const side = lichessGameSide(game, username);
    if (!side) return;
    const outcome = gameOutcome(game, side);
    if (outcome === "win") result.wins += 1;
    else if (outcome === "loss") result.losses += 1;
    else result.draws += 1;
  });
  return result;
}

function weakestOpeningFromGames(games, username) {
  const groups = new Map();
  (Array.isArray(games) ? games : []).forEach((game) => {
    const side = lichessGameSide(game, username);
    const name = openingName(game);
    if (!side || !name || /unknown opening/i.test(name)) return;
    const key = `${side}::${name}`;
    if (!groups.has(key)) groups.set(key, { name, color: side, games: 0, wins: 0, draws: 0, losses: 0, samples: [] });
    const row = groups.get(key);
    row.games += 1;
    const outcome = gameOutcome(game, side);
    if (outcome === "win") row.wins += 1;
    else if (outcome === "loss") row.losses += 1;
    else row.draws += 1;
    const moves = gameMoves(game);
    if (moves.length >= 4) row.samples.push({ outcome, moves });
  });
  const rows = [...groups.values()].map((row) => ({
    ...row,
    scoreRate: row.games ? Math.round(((row.wins + row.draws * 0.5) / row.games) * 100) : 0,
  }));
  const reliable = rows.filter((row) => row.games >= 3);
  const medium = rows.filter((row) => row.games >= 2);
  const candidates = reliable.length ? reliable : medium.length ? medium : rows;
  const weakest = [...candidates].sort((a, b) => a.scoreRate - b.scoreRate || b.losses - a.losses || b.games - a.games)[0] || null;
  if (!weakest) return null;
  const losingSample = weakest.samples.find((sample) => sample.outcome === "loss") || weakest.samples[0] || null;
  return { ...weakest, sampleMoves: losingSample?.moves || [] };
}

function normalizeSanLine(rawMoves, fen = START_FEN) {
  const tokens = Array.isArray(rawMoves)
    ? rawMoves.map((move) => String(move || "").trim()).filter(Boolean)
    : String(rawMoves || "").trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) return [];
  try {
    const game = new Chess(fen || START_FEN);
    const san = [];
    for (const token of tokens) {
      let move = null;
      try { move = game.move(token, { sloppy: true }); } catch {}
      if (!move && /^[a-h][1-8][a-h][1-8][qrbn]?$/i.test(token)) {
        try { move = game.move({ from: token.slice(0, 2), to: token.slice(2, 4), promotion: token[4] || "q" }); } catch {}
      }
      if (!move) break;
      san.push(move.san);
    }
    return san;
  } catch {
    return tokens;
  }
}

function unwrapPuzzle(payload) {
  if (!payload) return null;
  if (Array.isArray(payload)) return payload[0] || null;
  if (payload?.fenPosition || payload?.fen || payload?.after_pgn || payload?.afterPgn) return payload;
  for (const key of ["puzzle", "data", "result", "item"]) {
    const value = payload?.[key];
    if (value?.fenPosition || value?.fen || value?.after_pgn || value?.afterPgn) return value;
  }
  return null;
}

function puzzleUserColor(fen) {
  return String(fen || "").trim().split(/\s+/)[1] === "w" ? "b" : "w";
}

function nearestLevelIndex(elo) {
  const target = Number(elo) || 1600;
  let best = 0;
  let distance = Infinity;
  LEVELS.forEach((row, index) => {
    const next = Math.abs(row.elo - target);
    if (next < distance) { best = index; distance = next; }
  });
  return best;
}

async function fetchOpeningBook(fen) {
  const value = String(fen || "").trim();
  if (!value) return null;
  try {
    const response = await fetch(`${OPENING_EXPLORER}/lichess?variant=standard&fen=${encodeURIComponent(value)}&speeds[]=blitz&speeds[]=rapid&speeds[]=classical&ratings[]=1600&ratings[]=1800&ratings[]=2000&ratings[]=2200&ratings[]=2500`);
    if (!response.ok) return null;
    const data = await response.json();
    return {
      name: String(data?.opening?.name || "").trim(),
      eco: String(data?.opening?.eco || "").trim(),
      moves: (Array.isArray(data?.moves) ? data.moves : []).slice(0, 4).map((row) => ({
        san: String(row?.san || "").trim(),
        games: Number(row?.white || 0) + Number(row?.draws || 0) + Number(row?.black || 0),
      })).filter((row) => row.san),
    };
  } catch {
    return null;
  }
}

export default function HoodAIWeb({ variant = "alpha" }) {
  const { firebaseUser, user } = useContext(UserContext);
  const { language } = useLanguage();
  const qa = useOwnerQa();
  const { isPro, realIsPro } = usePro();
  const { session: paidSession, simulatedSession } = usePaidTraining();
  const betaMode = variant === "beta";
  const ownerQaMode = qa.isOwner && qa.active;
  const effectivePaidSession = paidSession || (ownerQaMode ? simulatedSession : null);
  const effectiveIsPro = ownerQaMode ? isPro : realIsPro;
  const lichessAllowed = !effectivePaidSession || effectivePaidSession.mode === "lichess";
  const boardRef = useRef(null);
  const sessionRef = useRef("");
  const sessionStartedRef = useRef(false);
  const engineTouchedRef = useRef(false);
  const lastReviewRef = useRef("");
  const latestBoardRef = useRef({ fen: START_FEN, pgn: "", ply: 0, userColor: "w", lines: [] });
  const latestOpeningRef = useRef(null);
  const openingCacheRef = useRef(new Map());
  const pendingActionRef = useRef("");
  const pendingCandidateRef = useRef(null);
  const recognitionRef = useRef(null);
  const chatEndRef = useRef(null);

  const [boardReady, setBoardReady] = useState(false);
  const [playerColor, setPlayerColor] = useState("w");
  const [engineLevelIndex, setEngineLevelIndex] = useState(nearestLevelIndex(1800));
  const [engineThinking, setEngineThinking] = useState(false);
  const [hoodThinking, setHoodThinking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [listening, setListening] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => getWebHoodVoiceEnabled());
  const [error, setError] = useState("");
  const [access, setAccess] = useState(null);
  const [mode, setMode] = useState("free_game");
  const [currentTask, setCurrentTask] = useState(null);
  const [opening, setOpening] = useState(null);
  const [lichessInput, setLichessInput] = useState("");
  const [lichessUser, setLichessUser] = useState("");
  const [lichessBusy, setLichessBusy] = useState(false);
  const [lichessRatings, setLichessRatings] = useState({});
  const [recent, setRecent] = useState(null);
  const [weakest, setWeakest] = useState(null);
  const [recentTournament, setRecentTournament] = useState(null);

  const engineLevel = LEVELS[engineLevelIndex] || LEVELS[5];
  const lang = ["pl", "es", "en"].includes(language) ? language : "en";

  const configuredRating = qa.isOwner && qa.active
    ? Number(qa.config.rating)
    : Number(user?.rating);
  const simulatedAccountAgeDays = qa.isOwner && qa.active
    ? Math.max(0, Number(qa.config.accountAgeDays) || 0)
    : null;
  const isZeroRatingPlayer = Number.isFinite(configuredRating) && configuredRating === 0;
  const beginnerProgressKey = `hood:zero-journey:v1:${firebaseUser?.uid || "guest"}`;
  const firstSessionKey = `hood:first-session:v1:${firebaseUser?.uid || "guest"}`;
  const beginnerProgress = useMemo(() => {
    if (typeof window === "undefined") return null;
    try { return JSON.parse(window.localStorage.getItem(beginnerProgressKey) || "null"); }
    catch { return null; }
  }, [beginnerProgressKey]);
  const beginnerJourneyStarted = Boolean(
    Number(beginnerProgress?.index || 0) > 0 ||
    (Array.isArray(beginnerProgress?.completed) && beginnerProgress.completed.length > 0)
  );
  const beginnerJourneyDone = Array.isArray(beginnerProgress?.completed) &&
    beginnerProgress.completed.length >= beginnerLessons.length;

  const [firstSession, setFirstSession] = useState(null);
  const [firstSessionLoaded, setFirstSessionLoaded] = useState(false);
  useEffect(() => {
    setFirstSessionLoaded(false);
    try {
      const saved = JSON.parse(window.localStorage.getItem(firstSessionKey) || "null");
      setFirstSession(saved && typeof saved === "object" ? saved : null);
    } catch {
      setFirstSession(null);
    } finally {
      setFirstSessionLoaded(true);
    }
  }, [firstSessionKey]);

  const hasExistingHoodActivity = useMemo(() => {
    if (typeof window === "undefined" || !firebaseUser?.uid) return false;
    try {
      const oldMessages = JSON.parse(window.localStorage.getItem(makeStorageId("hoodWebMessages:v2", firebaseUser.uid)) || "[]");
      const oldLichess = window.localStorage.getItem(makeStorageId("hoodLichess:web:v2", firebaseUser.uid)) || "";
      return (Array.isArray(oldMessages) && oldMessages.length > 0) || Boolean(oldLichess) || beginnerJourneyStarted;
    } catch {
      return beginnerJourneyStarted;
    }
  }, [beginnerJourneyStarted, firebaseUser?.uid]);

  const completeFirstSession = useCallback((payload) => {
    const next = { ...(payload || {}), completed: true };
    try { window.localStorage.setItem(firstSessionKey, JSON.stringify(next)); } catch {}
    const selectedElo = Number(next.selectedElo);
    if (Number.isFinite(selectedElo) && selectedElo >= 800) {
      const effectiveElo = qa.isOwner && qa.active
        ? Math.max(800, Math.min(3000, selectedElo))
        : (LEVELS[nearestLevelIndex(selectedElo)]?.elo || selectedElo);
      engineTouchedRef.current = true;
      setEngineLevelIndex(nearestLevelIndex(effectiveElo));
      try { window.localStorage.setItem(makeStorageId("hoodEngineElo:web:v2", firebaseUser?.uid), String(effectiveElo)); } catch {}
      boardRef.current?.setElo?.(effectiveElo);
    }
    setFirstSession(next);
  }, [firebaseUser?.uid, firstSessionKey, qa.active, qa.isOwner]);

  const ownerFreshVisit = Boolean(
    qa.isOwner &&
    qa.active &&
    simulatedAccountAgeDays !== null &&
    simulatedAccountAgeDays <= 1
  );

  useEffect(() => {
    if (!ownerFreshVisit) return;
    setFirstSession(null);
    try { window.localStorage.removeItem(firstSessionKey); } catch {}
    sessionStartedRef.current = false;
  }, [firstSessionKey, ownerFreshVisit]);

  const normalFirstTimeOnboarding = isZeroRatingPlayer &&
    !beginnerJourneyDone &&
    !beginnerJourneyStarted &&
    !hasExistingHoodActivity &&
    !firstSession?.completed;

  const showFirstTimeOnboarding = firstSessionLoaded && (
    ownerFreshVisit
      ? !firstSession?.completed
      : normalFirstTimeOnboarding
  );

  const showBeginnerJourney = firstSessionLoaded &&
    isZeroRatingPlayer &&
    !beginnerJourneyDone &&
    !showFirstTimeOnboarding &&
    (
      qa.isOwner && qa.active
        ? true
        : firstSession?.path !== "improve"
    );

  const lifecycleStage = simulatedAccountAgeDays === null
    ? "regular"
    : simulatedAccountAgeDays <= 1
      ? "first_visit"
      : simulatedAccountAgeDays <= 7
        ? "early"
        : simulatedAccountAgeDays < 30
          ? "returning"
          : "regular";
  const simplifiedEarlyExperience = qa.isOwner && qa.active && lifecycleStage === "early";

  const handleTrainingLevelChange = useCallback((elo) => {
    const raw = Math.max(0, Math.min(3000, Number(elo) || 0));

    if (qa.isOwner) {
      qa.setEnabled(true);
      qa.setRating(raw);
    }

    if (raw === 0) {
      engineTouchedRef.current = true;
      setEngineLevelIndex(nearestLevelIndex(800));
      boardRef.current?.setElo?.(800);
      if (firebaseUser?.uid) {
        try { window.localStorage.setItem(makeStorageId("hoodEngineElo:web:v2", firebaseUser.uid), "800"); } catch {}
      }
      return;
    }

    const exactEngineElo = Math.max(800, raw);
    engineTouchedRef.current = true;
    setEngineLevelIndex(nearestLevelIndex(exactEngineElo));
    boardRef.current?.setElo?.(exactEngineElo);
    if (firebaseUser?.uid) {
      try { window.localStorage.setItem(makeStorageId("hoodEngineElo:web:v2", firebaseUser.uid), String(exactEngineElo)); } catch {}
    }
    if (showBeginnerJourney) {
      completeFirstSession({
        path: "improve",
        selectedElo: exactEngineElo,
        completedAt: new Date().toISOString(),
      });
    }
  }, [completeFirstSession, firebaseUser?.uid, qa, showBeginnerJourney]);

  const handleAccountAgeChange = useCallback((days) => {
    if (!qa.isOwner) return;
    const nextDays = Math.max(0, Math.min(365, Math.round(Number(days) || 0)));
    qa.setEnabled(true);
    qa.setAccountAgeDays(nextDays);
    if (nextDays <= 1) {
      setFirstSession(null);
      sessionStartedRef.current = false;
      try { window.localStorage.removeItem(firstSessionKey); } catch {}
    }
  }, [firstSessionKey, qa]);

  const appendMessage = useCallback((role, text) => {
    const clean = String(text || "").trim();
    if (!clean) return;
    setMessages((current) => [
      ...current,
      { id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, role, text: clean },
    ].slice(-100));
  }, [mode, variant]);

  useEffect(() => {
    if (!firebaseUser?.uid) return;
    sessionRef.current = sessionIdFor(firebaseUser.uid);
    const messageKey = makeStorageId("hoodWebMessages:v2", firebaseUser.uid);
    const lichessKey = makeStorageId("hoodLichess:web:v2", firebaseUser.uid);
    const eloKey = makeStorageId("hoodEngineElo:web:v2", firebaseUser.uid);
    try {
      const savedMessages = JSON.parse(window.localStorage.getItem(messageKey) || "[]");
      if (Array.isArray(savedMessages) && savedMessages.length) setMessages(savedMessages.slice(-100));
      const savedLichess = window.localStorage.getItem(lichessKey) || "";
      if (savedLichess && lichessAllowed) { setLichessUser(savedLichess); setLichessInput(savedLichess); }
      const savedElo = Number(window.localStorage.getItem(eloKey));
      if (Number.isFinite(savedElo) && savedElo >= 800) {
        engineTouchedRef.current = true;
        setEngineLevelIndex(nearestLevelIndex(savedElo));
      }
    } catch {}
  }, [firebaseUser?.uid, lichessAllowed]);

  useEffect(() => {
    if (!firebaseUser?.uid || !messages.length) return;
    try { window.localStorage.setItem(makeStorageId("hoodWebMessages:v2", firebaseUser.uid), JSON.stringify(messages.slice(-100))); } catch {}
    chatEndRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [firebaseUser?.uid, messages]);

  useEffect(() => {
    const effectiveEngineElo = qa.isOwner && qa.active
      ? Math.max(800, Math.min(3000, Number(qa.config.rating) || 0))
      : engineLevel.elo;
    if (boardReady) boardRef.current?.setElo?.(effectiveEngineElo);
    if (firebaseUser?.uid) {
      try { window.localStorage.setItem(makeStorageId("hoodEngineElo:web:v2", firebaseUser.uid), String(effectiveEngineElo)); } catch {}
    }
  }, [boardReady, engineLevel.elo, firebaseUser?.uid, qa.active, qa.config.rating, qa.isOwner]);

  useEffect(() => {
    if (!qa.isOwner || !qa.active) return;
    const simulatedElo = Math.max(800, Math.min(3000, Number(qa.config.rating) || 0));
    engineTouchedRef.current = true;
    setEngineLevelIndex(nearestLevelIndex(simulatedElo));
    boardRef.current?.setElo?.(simulatedElo);
  }, [qa.active, qa.config.rating, qa.isOwner]);

  useEffect(() => {
    if (!isZeroRatingPlayer || !beginnerJourneyDone) return;
    const nextIndex = nearestLevelIndex(800);
    engineTouchedRef.current = true;
    setEngineLevelIndex(nextIndex);
    boardRef.current?.setElo?.(800);
  }, [beginnerJourneyDone, isZeroRatingPlayer]);

  const releaseBoard = useCallback(() => {
    try { boardRef.current?.releaseEngineReply?.(); } catch {}
  }, []);

  const speak = useCallback((text, after = null) => {
    const done = () => {
      releaseBoard();
      if (typeof after === "function") Promise.resolve(after()).catch(() => {});
    };
    if (!voiceOn) {
      done();
      return;
    }
    speakWebHoodVoice(text, lang, { onEnd: done });
  }, [lang, releaseBoard, voiceOn]);

  const appendHoodMessage = useCallback((text, after = null) => {
    const clean = String(text || "").trim();
    if (!clean) {
      if (typeof after === "function") Promise.resolve(after()).catch(() => {});
      return;
    }
    appendMessage("hood", clean);
    speak(clean, after);
  }, [appendMessage, speak]);


  const getOpeningForFen = useCallback(async (fen) => {
    const key = String(fen || "").trim();
    if (!key) return null;
    const fullMove = Number(key.split(/\s+/)[5] || 1);
    if (fullMove > 16) return null;
    if (openingCacheRef.current.has(key)) return openingCacheRef.current.get(key);
    const value = await fetchOpeningBook(key);
    openingCacheRef.current.set(key, value);
    if (openingCacheRef.current.size > 80) openingCacheRef.current.delete(openingCacheRef.current.keys().next().value);
    return value;
  }, []);

  const postHood = useCallback(async (payload) => {
    if (ownerQaMode && !effectivePaidSession) {
      const gate = qa.claimHoodAccess();
      if (!gate?.allowed) {
        const err = new Error(gate?.detail || "QA Free Hood AI window is locked.");
        err.status = 403;
        err.data = { requiresPro: true, access: gate };
        setAccess({ requiresPro: true, allowed: false, message: gate?.detail, nextAvailableAt: gate?.nextAvailableAt || null });
        throw err;
      }
    }
    const current = auth.currentUser;
    if (!current) throw new Error("Hood AI requires an active login");
    if (paidSession) {
      const data = await trainingRequest("coach", { body: { ...payload, lang }, sessionId: paidSession.id });
      setAccess(data.access || null);
      return data;
    }
    const token = await current.getIdToken();
    const data = await requestHoodCoach({ payload, token, lang, betaMode });
    setAccess(data?.access || null);
    if (data?.sessionId) {
      sessionRef.current = String(data.sessionId);
      try { window.localStorage.setItem(makeStorageId("hoodTrainerSessionId:web:v1", firebaseUser?.uid), sessionRef.current); } catch {}
    }
    return data || {};
  }, [betaMode, effectivePaidSession, firebaseUser?.uid, lang, ownerQaMode, paidSession, qa]);

  const actionPolicy = useCallback((event) => [
    "Hood Action Engine V1 can control the Hood board without changing the normal Training screen.",
    `Current trainer event: ${event}.`,
    ["session_start", "user_message", "resume"].includes(event)
      ? "You may choose ONE next action when useful. Put exactly one directive at the START: [[HOOD_ACTION:ASK]], [[HOOD_ACTION:PUZZLE]], [[HOOD_ACTION:POSITION]], or [[HOOD_ACTION:OPENING_DRILL]]. If no board action is needed, output no directive."
      : "For this event output NO HOOD_ACTION directive.",
    "ASK means ask exactly one short question and wait for the answer.",
    "PUZZLE means load one personalised Chesshood Training puzzle without revealing the solution.",
    "POSITION means load one personalised database position for calculation and discussion.",
    "OPENING_DRILL means restart the player's current weakest/relevant Lichess opening line.",
    "Use evidence-based variety and never invent board facts.",
  ].join(" "), []);

  const naturalCoachPolicy = useMemo(() => [
    "You are the same long-term personal chess coach, not a fresh chatbot session.",
    "Use stored conversation memory and Lichess context when supported; never re-introduce yourself after the first meeting.",
    "Board and Stockfish facts are the source of truth. Never praise a move labelled inaccuracy, mistake, or blunder.",
    "Usually reply in 6-18 words for move coaching. Make one practical coaching point at a time.",
    "For move-by-move coaching, name the move when available, say what it changed, and give one practical next idea.",
    "When an opening is relevant, say its full human-readable name naturally, never only an ECO code.",
    "Never end a visible answer with an ellipsis. Finish the sentence.",
    "Do not reveal puzzle or opening-drill solutions unless the player has already played them or explicitly asks after the exercise.",
  ].join(" "), []);

  const directPolicy = useMemo(() => [
    "This is a direct conversation with the player. Answer the actual question first.",
    "Reply in the player's language.",
    "Normally use 2-4 short sentences, around 25-60 words, unless detail is requested.",
    "Sound like an experienced human chess coach and explain why the advice matters.",
    "Use supplied board, Stockfish, task and Lichess facts as evidence. Never invent legal moves, evaluations, statistics or game history.",
    "When useful, add one concrete candidate move, short variation, positional plan, tactical motif or training tip.",
    "Avoid empty praise, slogans and customer-support language.",
  ].join(" "), []);

  const runAction = useCallback(async (action) => {
    if (!action || !boardReady) return false;
    if (action === "ASK") return true;
    if (action === "OPENING_DRILL") {
      if (!weakest?.sampleMoves?.length) {
        const sentence = lang === "pl"
          ? "Połącz Lichess, żebym mógł wybrać Twoje najsłabsze otwarcie."
          : lang === "es"
            ? "Conecta Lichess para que pueda elegir tu apertura más débil."
            : "Connect Lichess so I can choose your weakest opening.";
        appendHoodMessage(sentence);
        return false;
      }
      const moves = normalizeSanLine(weakest.sampleMoves, START_FEN);
      const color = weakest.color === "black" ? "b" : "w";
      if (!moves.length) return false;
      setMode("opening_drill");
      trackUsageEvent("lesson_start", { meta: { mode: "opening_drill", category: weakest.name, variant } });
      setCurrentTask({ title: weakest.name, color, moves, motif: "opening", actionMode: "OPENING_DRILL" });
      setPlayerColor(color);
      boardRef.current?.startPractice?.(moves, color, START_FEN);
      return true;
    }
    if (action === "PUZZLE" || action === "POSITION") {
      try {
        const raw = await getFirstPuzzle(lang);
        const puzzle = unwrapPuzzle(raw);
        const fen = String(puzzle?.fenPosition || puzzle?.fen || "").trim();
        const rawMoves = puzzle?.after_pgn ?? puzzle?.afterPgn ?? puzzle?.moves;
        const moves = normalizeSanLine(rawMoves, fen);
        if (!fen || moves.length < 2) throw new Error("Invalid personalised training payload");
        const color = puzzleUserColor(fen);
        setMode(action === "POSITION" ? "position" : "puzzle");
        trackUsageEvent("puzzle_start", {
          meta: {
            mode: action === "POSITION" ? "position" : "puzzle",
            category: Array.isArray(puzzle?.categories) ? puzzle.categories[0] || "" : "",
            variant,
          },
        });
        setCurrentTask({
          title: tr(lang, action === "POSITION" ? COPY.position : COPY.puzzle),
          color,
          moves,
          fen,
          motif: "database_puzzle",
          actionMode: action,
          puzzleId: String(puzzle?.id || puzzle?._id?.$oid || puzzle?._id || ""),
          categories: Array.isArray(puzzle?.categories) ? puzzle.categories : [],
        });
        setPlayerColor(color);
        boardRef.current?.startPractice?.(moves, color, fen);
        return true;
      } catch (actionError) {
        setError(actionError?.message || "Could not load personalised training.");
        return false;
      }
    }
    return false;
  }, [appendHoodMessage, boardReady, lang, weakest]);

  const coach = useCallback(async (event, extra = {}) => {
    if (!firebaseUser || hoodThinking) {
      if (event === "move_correct" || event === "move_wrong") releaseBoard();
      return "";
    }
    setHoodThinking(true);
    setError("");
    try {
      const boardFacts = { ...latestBoardRef.current, ...(extra.board || {}) };
      const openingData = await getOpeningForFen(boardFacts.fen);
      if (openingData) {
        latestOpeningRef.current = openingData;
        setOpening(openingData);
      }
      const bestLine = Array.isArray(boardFacts?.lines) ? boardFacts.lines[0] || {} : {};
      const board = {
        ...boardFacts,
        opening: openingData || latestOpeningRef.current || undefined,
        openingName: openingData?.name || latestOpeningRef.current?.name || "",
        openingEco: openingData?.eco || latestOpeningRef.current?.eco || "",
        bookMoves: openingData?.moves || latestOpeningRef.current?.moves || [],
        evaluation: boardFacts?.evaluation || humanEngineAssessment(bestLine?.cp ?? boardFacts?.afterCp, bestLine?.mate ?? boardFacts?.afterMate, lang),
        bestMove: boardFacts?.bestMove || bestLine?.pv?.[0] || "",
        pv: boardFacts?.pv || (Array.isArray(bestLine?.pv) ? bestLine.pv.slice(0, 6).join(" ") : ""),
        engineSource: "Stockfish 16",
        positionSource: "HoodEngineBoardWeb",
      };
      const actionAllowed = ["session_start", "user_message", "resume"].includes(event);
      const task = currentTask || { title: tr(lang, COPY.freeGame), color: playerColor, motif: "free_game", actionMode: "" };
      const lichessProfile = lichessUser ? {
        username: lichessUser,
        ratings: lichessRatings,
        recent10: recent || undefined,
        worstOpenings: weakest ? [weakest] : [],
        recentTournament: recentTournament || undefined,
      } : undefined;
      const response = await postHood({
        sessionId: sessionRef.current || undefined,
        event,
        lichessProfile,
        task: {
          title: task.title || "",
          topic: task.title || opening?.name || "live chess coaching",
          opening: weakest?.name || opening?.name || "",
          inferredWeakestOpening: weakest?.name || "",
          weakestOpeningStats: weakest || undefined,
          color: task.color || playerColor,
          expectedMove: "",
          motif: task.motif || "",
          actionMode: task.actionMode || "",
          puzzleId: task.puzzleId || "",
          categories: task.categories || [],
          lineLength: Array.isArray(task.moves) ? task.moves.length : 0,
          sessionProgress: { phase: mode === "free_game" ? "stockfish_game" : "drill" },
          playerRating: qa.isOwner && qa.active
            ? qa.config.rating
            : (lichessRatings.rapid || lichessRatings.classical || lichessRatings.blitz || undefined),
          accountAgeDays: qa.isOwner && qa.active ? qa.config.accountAgeDays : undefined,
          lifecycleStage: qa.isOwner && qa.active ? lifecycleStage : undefined,
        },
        board,
        playerMessage: event === "user_message" ? String(extra.message || "").trim() : undefined,
        message: event === "user_message"
          ? String(extra.message || "").trim()
          : [naturalCoachPolicy, actionPolicy(event), extra.message || ""].filter(Boolean).join("\n"),
        coachPolicy: event === "user_message" ? directPolicy : undefined,
        lichessConnected: Boolean(lichessUser),
        promptType: extra.promptType || "coach_chat",
      });
      const raw = String(response?.speech || response?.coachText || response?.reply || response?.message || response?.answer || "").trim();
      const parsed = parseHoodSpeech(raw);
      pendingActionRef.current = actionAllowed ? parsed.action : "";
      const afterSpeech = pendingActionRef.current
        ? async () => { const next = pendingActionRef.current; pendingActionRef.current = ""; await runAction(next); }
        : null;
      if (parsed.speech) appendHoodMessage(parsed.speech, afterSpeech);
      else {
        releaseBoard();
        if (afterSpeech) await afterSpeech();
      }
      return parsed.speech;
    } catch (coachError) {
      if (!(coachError?.status === 403 && coachError?.data?.requiresPro)) setError(coachError?.message || "Hood AI is unavailable right now.");
      releaseBoard();
      return "";
    } finally {
      setHoodThinking(false);
    }
  }, [actionPolicy, appendHoodMessage, betaMode, currentTask, directPolicy, firebaseUser, getOpeningForFen, hoodThinking, lang, lichessRatings, lichessUser, mode, naturalCoachPolicy, opening?.name, playerColor, postHood, qa.active, qa.config.rating, recent, recentTournament, releaseBoard, runAction, weakest]);

  const handleBoard = useCallback((msg) => {
    if (!msg?.type) return;
    const type = msg.type;
    if (type === "thinking") {
      setEngineThinking(Boolean(msg.value));
      return;
    }
    if (["turn", "move", "analysis", "moveReview", "gameOver"].includes(type)) {
      latestBoardRef.current = { ...latestBoardRef.current, ...msg };
    }
    if (type === "analysis") {
      const best = Array.isArray(msg?.lines) ? msg.lines[0] || {} : {};
      latestBoardRef.current = { ...latestBoardRef.current, ...msg, evaluation: humanEngineAssessment(best?.cp, best?.mate, lang), bestMove: best?.pv?.[0] || "", pv: Array.isArray(best?.pv) ? best.pv.join(" ") : "" };
      return;
    }
    if (type === "move") {
      if (msg?.fen) getOpeningForFen(msg.fen).then((value) => { if (value) { latestOpeningRef.current = value; setOpening(value); } });
      return;
    }
    if (type === "moveReview") {
      const reviewId = String(msg?.reviewId || `${msg?.ply || ""}:${msg?.san || ""}:${msg?.fen || ""}`);
      if (reviewId && reviewId === lastReviewRef.current) return;
      lastReviewRef.current = reviewId;
      const verdict = moveVerdict(msg?.cpLoss);
      const event = ["blunder", "mistake", "inaccuracy"].includes(verdict) ? "move_wrong" : "move_correct";
      coach(event, {
        board: { ...latestBoardRef.current, ...msg, verdict },
        message: `Coach the player's move ${msg?.san || ""}. Stockfish verdict=${verdict}. cpLoss=${msg?.cpLoss ?? "unknown"}. Keep it short and concrete.`,
        promptType: "move_review",
      });
      return;
    }
    if (type === "gameOver") {
      const reason = String(msg?.reason || "game_over");
      const side = msg?.userColor || latestBoardRef.current?.userColor || playerColor;
      const result = reason === "draw"
        ? "draw"
        : (reason === "white_wins" && side === "w") || (reason === "black_wins" && side === "b")
          ? "win"
          : "loss";
      trackUsageEvent("game_end", {
        meta: {
          result,
          mode,
          rating: qa.isOwner && qa.active ? qa.config.rating : engineLevel.elo,
          variant,
        },
      });
      coach("resume", { board: { ...latestBoardRef.current, ...msg }, message: `The game just ended: ${msg?.reason || "game over"}. Give one short summary and choose the next useful training step.` });
      return;
    }
    if (type === "candidateEval" && pendingCandidateRef.current) {
      const accept = Number.isFinite(Number(msg?.cpLoss)) ? Number(msg.cpLoss) <= 55 : false;
      const pending = pendingCandidateRef.current;
      pendingCandidateRef.current = null;
      boardRef.current?.resolveWrongCandidate?.(accept);
      const sentence = accept
        ? (lang === "pl" ? `${pending.san} też jest dobrym ruchem — wychodzimy z zapamiętanej linii.` : lang === "es" ? `${pending.san} también es una buena jugada; salimos de la línea memorizada.` : `${pending.san} is also a good move — we can leave the memorised line.`)
        : (lang === "pl" ? `Nie tym razem. Wróć do pozycji i znajdź ruch z idei otwarcia.` : lang === "es" ? `Esta vez no. Vuelve a la posición y encuentra la idea de la apertura.` : `Not this time. Return to the position and find the opening idea.`);
      appendHoodMessage(sentence);
    }
  }, [appendHoodMessage, coach, engineLevel.elo, getOpeningForFen, lang, mode, playerColor, qa.active, qa.config.rating, qa.isOwner, variant]);

  const handleWrongCandidate = useCallback((msg) => {
    if (!msg?.san) return;
    trackUsageEvent("puzzle_result", { meta: { result: "wrong_attempt", mode, variant } });
    const reqId = `candidate-${Date.now()}`;
    pendingCandidateRef.current = { reqId, san: msg.san, expected: msg.expected };
    boardRef.current?.evaluateCandidate?.(msg.san, reqId);
  }, [mode, variant]);

  const handlePracticeGuide = useCallback((msg) => {
    if (!msg) return;
    if (msg.done) {
      if (mode === "puzzle" || mode === "position") {
        trackUsageEvent("puzzle_result", { meta: { result: "solved", mode, variant } });
      } else if (mode === "opening_drill") {
        trackUsageEvent("lesson_complete", { meta: { mode, category: currentTask?.title || "", variant } });
      }
      return;
    }
    if (!msg.expected) return;
    const key = `${mode}:${msg.ply}:${msg.expected}`;
    if (latestBoardRef.current?.practiceGuideKey === key) return;
    latestBoardRef.current = { ...latestBoardRef.current, practiceGuideKey: key };
    const line = lang === "pl" ? `Teraz ${msg.expected}.` : lang === "es" ? `Ahora ${msg.expected}.` : `Now ${msg.expected}.`;
    appendHoodMessage(line);
  }, [appendHoodMessage, currentTask?.title, lang, mode, variant]);

  const handleReady = useCallback(() => {
    setBoardReady(true);
    boardRef.current?.setElo?.(isZeroRatingPlayer && beginnerJourneyDone ? 800 : engineLevel.elo);
    if (!sessionStartedRef.current) {
      sessionStartedRef.current = true;
      trackUsageEvent("game_start", {
        meta: {
          mode: "free_game",
          rating: qa.isOwner && qa.active ? qa.config.rating : engineLevel.elo,
          variant,
        },
      });
      const openingMessage = isZeroRatingPlayer && beginnerJourneyDone
        ? "The player has just completed the full zero-to-beginner course and this is their first complete chess game. Do not ask what they want to train. Guide the game yourself, one simple idea at a time. Explain whose turn it is, what to look for, and let the player make every move. Keep language extremely simple and never assume chess knowledge beyond the completed beginner course."
        : "Begin or resume this Hood coaching session naturally. If the player's durable chess goal is unknown, ask one short goal question before choosing a drill.";
      setTimeout(() => coach("session_start", { message: openingMessage }), 250);
    }
  }, [beginnerJourneyDone, coach, engineLevel.elo, isZeroRatingPlayer, qa.active, qa.config.rating, qa.isOwner, variant]);

  const connectLichess = useCallback(async () => {
    if (!lichessAllowed) return;
    const username = String(lichessInput || lichessUser || "").trim();
    if (!username) return;
    setLichessBusy(true);
    setError("");
    try {
      let profile, games, tournamentRows = [];
      if (paidSession) {
        const data = await trainingRequest("lichess", { body: { username }, sessionId: paidSession.id });
        profile = data.profile; games = data.games; tournamentRows = data.tournaments || [];
      } else {
        const profileResponse = await fetch(`https://lichess.org/api/user/${encodeURIComponent(username)}`);
        if (!profileResponse.ok) throw new Error("Lichess user not found.");
        profile = await profileResponse.json();
        const gamesResponse = await fetch(`https://lichess.org/api/games/user/${encodeURIComponent(username)}?max=120&opening=true&pgnInJson=true&clocks=false&evals=false`, { headers: { Accept: "application/x-ndjson" } });
        if (!gamesResponse.ok) throw new Error("Lichess games are temporarily unavailable.");
        games = parseNdjson(await gamesResponse.text());
      }
      const canonical = String(profile?.username || username).trim();
      const ratings = {
        classical: Number(profile?.perfs?.classical?.rating || 0) || null,
        rapid: Number(profile?.perfs?.rapid?.rating || 0) || null,
        blitz: Number(profile?.perfs?.blitz?.rating || 0) || null,
      };
      const nextRecent = recentTen(games, canonical);
      const nextWeakest = weakestOpeningFromGames(games, canonical);
      let tournament = null;
      if (tournamentRows.length) {
        const rows = tournamentRows;
        const first = rows[0];
        const tour = first?.tournament || first?.tour || first || {};
        const player = first?.player || first?.result || first || {};
        if (first) tournament = {
          name: tour?.fullName || tour?.name || "Arena",
          rank: player?.rank ?? player?.position ?? null,
          score: player?.score ?? player?.points ?? null,
          games: player?.nbGames ?? player?.games ?? null,
          performance: player?.performance ?? first?.performance ?? null,
        };
      }
      setLichessUser(canonical);
      setLichessInput(canonical);
      setLichessRatings(ratings);
      setRecent(nextRecent);
      setWeakest(nextWeakest);
      setRecentTournament(tournament);
      try { window.localStorage.setItem(makeStorageId("hoodLichess:web:v2", firebaseUser?.uid), canonical); } catch {}
      const primary = ratings.rapid || ratings.classical || ratings.blitz || 0;
      if (!engineTouchedRef.current && primary) {
        const nextIndex = nearestLevelIndex(Math.min(2600, Math.max(800, primary + 100)));
        setEngineLevelIndex(nextIndex);
        boardRef.current?.setElo?.(LEVELS[nextIndex].elo);
      }
      const sentence = nextWeakest
        ? (lang === "pl" ? `Połączyłem @${canonical}. Najsłabsze otwarcie: ${nextWeakest.name} — ${nextWeakest.scoreRate}% z ${nextWeakest.games} partii.` : lang === "es" ? `He conectado @${canonical}. Apertura más débil: ${nextWeakest.name}, ${nextWeakest.scoreRate}% en ${nextWeakest.games} partidas.` : `Connected @${canonical}. Weakest opening: ${nextWeakest.name} — ${nextWeakest.scoreRate}% across ${nextWeakest.games} games.`)
        : (lang === "pl" ? `Połączyłem konto @${canonical} i pobrałem Twoje ostatnie partie.` : lang === "es" ? `He conectado @${canonical} y descargado tus partidas recientes.` : `Connected @${canonical} and downloaded your recent games.`);
      appendHoodMessage(sentence);
    } catch (lichessError) {
      setError(lichessError?.message || "Could not connect Lichess.");
    } finally {
      setLichessBusy(false);
    }
  }, [appendHoodMessage, firebaseUser?.uid, lang, lichessInput, lichessUser, lichessAllowed, paidSession]);

  const autoConnectAttempted = useRef("");
  useEffect(() => {
    const key = `${firebaseUser?.uid}:${lichessUser}`;
    if (lichessAllowed && firebaseUser?.uid && lichessUser && !recent && !lichessBusy && autoConnectAttempted.current !== key) {
      autoConnectAttempted.current = key;
      connectLichess();
    }
  }, [connectLichess, firebaseUser?.uid, lichessBusy, lichessUser, recent, lichessAllowed]);

  const sendQuestion = useCallback(async (override = "") => {
    const question = String(override || input).trim();
    if (!question || hoodThinking) return;
    setInput("");
    trackUsageEvent("hood_message", { meta: { mode, variant } });
    appendMessage("user", question);
    await coach("user_message", { message: question, board: latestBoardRef.current, promptType: "direct_chat" });
  }, [appendMessage, coach, hoodThinking, input, mode, variant]);

  const toggleMic = useCallback(() => {
    if (listening) {
      try { recognitionRef.current?.stop(); } catch {}
      setListening(false);
      return;
    }
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      setError(lang === "pl" ? "Ta przeglądarka nie obsługuje rozpoznawania mowy." : lang === "es" ? "Este navegador no admite reconocimiento de voz." : "Speech recognition is not supported by this browser.");
      return;
    }
    try {
      const recognition = new Recognition();
      recognition.lang = lang === "pl" ? "pl-PL" : lang === "es" ? "es-ES" : "en-GB";
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.onresult = (event) => {
        const text = event?.results?.[0]?.[0]?.transcript || "";
        setListening(false);
        if (text) sendQuestion(text);
      };
      recognition.onerror = () => setListening(false);
      recognition.onend = () => setListening(false);
      recognitionRef.current = recognition;
      setListening(true);
      recognition.start();
    } catch {
      setListening(false);
    }
  }, [lang, listening, sendQuestion]);

  const changeEngineLevel = useCallback((delta) => {
    engineTouchedRef.current = true;
    setEngineLevelIndex((current) => {
      const next = Math.max(0, Math.min(LEVELS.length - 1, current + delta));
      boardRef.current?.setElo?.(LEVELS[next].elo);
      return next;
    });
  }, []);

  const startFreeGame = useCallback(() => {
    setMode("free_game");
    setCurrentTask(null);
    setPlayerColor("w");
    boardRef.current?.setUserColor?.("w");
  }, []);

  const profileName = user?.fullName || user?.name || firebaseUser?.displayName || firebaseUser?.email?.split("@")[0] || "Chesshood player";
  const primaryRating = qa.isOwner && qa.active
    ? qa.config.rating
    : (lichessRatings.rapid || lichessRatings.classical || lichessRatings.blitz || configuredRating || null);
  const planTitle = mode === "opening_drill"
    ? `${tr(lang, COPY.drill)} · ${currentTask?.title || weakest?.name || ""}`
    : mode === "puzzle"
      ? tr(lang, COPY.puzzle)
      : mode === "position"
        ? tr(lang, COPY.position)
        : tr(lang, COPY.freeGame);

  const qaAccess = ownerQaMode && !effectivePaidSession
    ? qa.hoodAccess
    : null;
  const simulatedAccess = qaAccess
    ? {
        requiresPro: !qaAccess.allowed,
        allowed: qaAccess.allowed,
        message: qaAccess.detail,
        nextAvailableAt: qaAccess.nextAvailableAt || null,
      }
    : access;
  const accessBlocked = Boolean(simulatedAccess?.requiresPro && simulatedAccess?.allowed === false);

  if (showFirstTimeOnboarding) {
    return (
      <HoodFirstTimeOnboarding
        lang={lang}
        userId={firebaseUser?.uid || "guest"}
        variantLabel={betaMode ? "BETA" : "ALPHA"}
        onComplete={completeFirstSession}
        ownerMode={qa.isOwner}
        ownerAccountAgeDays={simulatedAccountAgeDays ?? 0}
        onLevelChange={qa.isOwner ? handleTrainingLevelChange : undefined}
        onAccountAgeChange={qa.isOwner ? handleAccountAgeChange : undefined}
      />
    );
  }

  if (showBeginnerJourney) {
    return (
      <HoodBeginnerTraining
        guidedJourney
        variantLabel={betaMode ? "BETA" : "ALPHA"}
        returnTo={betaMode ? "/hood-ai-beta" : "/hood-ai-alpha"}
        onChangeLevel={handleTrainingLevelChange}
        showLevelControl={qa.isOwner}
        currentLevel={qa.active ? qa.config.rating : 0}
        accountAgeDays={simulatedAccountAgeDays ?? 0}
        onAccountAgeChange={qa.isOwner ? handleAccountAgeChange : undefined}
      />
    );
  }

  return (
    <div className="hood-ai-web-page" style={{ minHeight: "100vh", background: BG, color: TEXT, padding: "16px 12px 46px" }}>
      <style>{`
        .hood-ai-board-card{background:rgba(8,13,28,.94);border:1px solid rgba(255,212,0,.30);border-radius:20px;padding:10px;overflow:hidden}
        @media(max-width:768px){
          .hood-ai-web-page{padding-left:12px!important;padding-right:12px!important}
          .hood-ai-board-card{
            width:100vw;
            margin-left:calc(50% - 50vw);
            border-left:0;
            border-right:0;
            border-radius:0;
            padding:0;
          }
          .hood-ai-board-controls{padding:8px 10px 0}
          .hood-ai-board-status{padding:4px 10px 8px}
        }
      `}</style>
      <div style={{ maxWidth: 1020, margin: "0 auto" }}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <Link to="/" style={{ color: TEXT, textDecoration: "none", fontWeight: 900 }}>← Chesshood</Link>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ color: MUTED, fontSize: 13 }}>{profileName}</span>
            {qa.isOwner && <HoodLevelPicker
              lang={lang}
              currentElo={qa.active ? qa.config.rating : engineLevel.elo}
              compact
              allowBeginner
              ownerQa
              accountAgeDays={qa.active ? qa.config.accountAgeDays : 30}
              onAccountAgeChange={handleAccountAgeChange}
              onSelect={handleTrainingLevelChange}
            />}
            <HoodVoiceControls lang={lang} compact enabled={voiceOn} onEnabledChange={setVoiceOn} />
            <span style={{ border: `1px solid ${effectiveIsPro ? GOLD : "rgba(255,255,255,.18)"}`, color: effectiveIsPro ? GOLD : MUTED, borderRadius: 999, padding: "6px 10px", fontWeight: 900, fontSize: 12 }}>
              <FontAwesomeIcon icon={faCrown} style={{ marginRight: 6 }} />{effectivePaidSession ? (effectivePaidSession.qa ? "QA PAID SESSION" : (lang === "pl" ? "OPŁACONA SESJA" : lang === "es" ? "SESIÓN PAGADA" : "PAID SESSION")) : effectiveIsPro ? tr(lang, COPY.pro) : tr(lang, COPY.free)}
            </span>
            {!effectiveIsPro && !effectivePaidSession && <Link to="/training" style={{ background: GOLD, color: "#111827", borderRadius: 999, padding: "7px 12px", textDecoration: "none", fontWeight: 900, fontSize: 12 }}>{tr(lang, COPY.upgrade)}</Link>}
            <ResponsiveAppMenu />
          </div>
        </header>

        <section style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 0 18px", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
          <div style={{ width: 62, height: 62, display: "grid", placeItems: "center", borderRadius: 18, background: "rgba(255,212,0,.08)", border: `1px solid ${BORDER}`, color: GOLD, fontSize: 29 }}>
            <FontAwesomeIcon icon={faChessKnight} />
          </div>
          <div>
            <div style={{ color: GOLD, fontWeight: 900, fontSize: 12, letterSpacing: ".14em" }}>{tr(lang, COPY.coach)}</div>
            <h1 style={{ margin: "4px 0 5px", fontSize: "clamp(1.7rem,4vw,2.45rem)" }}>Hood AI <span style={{fontSize:12,color: betaMode ? "#7dd3fc" : "#d9cf93",verticalAlign:"middle"}}>{betaMode ? "BETA · PRIVATE QA" : "ALPHA"}</span></h1>
            <div style={{ color: MUTED, lineHeight: 1.5 }}>{tr(lang, COPY.decides)}</div>
          </div>
        </section>

        <div style={{ marginTop: 14 }}>
          <HoodCareerPath
            rating={primaryRating || 0}
            lang={lang}
            accountAgeDays={simulatedAccountAgeDays}
            userId={firebaseUser?.uid || "guest"}
            qa={qa.isOwner && qa.active}
          />
        </div>

        {betaMode && qa.isOwner && (
          <section style={{ marginTop: 16, border: "1px solid rgba(56,189,248,.55)", borderRadius: 18, background: "rgba(14,116,144,.13)", padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div>
                <div style={{ color: "#7dd3fc", fontWeight: 900, fontSize: 13, letterSpacing: ".08em" }}>PRIVATE BETA · OWNER QA</div>
                <div style={{ color: TEXT, fontWeight: 900, fontSize: 18, marginTop: 4 }}>
                  {qa.active ? `${qa.config.subscriptionMode.toUpperCase()} · rating ${qa.config.rating} · day ${qa.config.accountAgeDays}` : "QA is off — using your real account"}
                </div>
                <div style={{ color: MUTED, marginTop: 4, lineHeight: 1.45 }}>
                  {qa.active ? (qa.hoodAccess?.detail || "QA simulation active.") : "Turn QA on to simulate a different player, subscription and payments."}
                </div>
              </div>
              <Link to="/owner-qa" style={{ ...smallButton, textDecoration: "none", display: "inline-block" }}>Open QA Lab</Link>
            </div>
          </section>
        )}

        {accessBlocked && (
          <section style={{ marginTop: 16, border: `1px solid ${BORDER}`, borderRadius: 18, background: "rgba(255,212,0,.07)", padding: 16 }}>
            <div style={{ color: GOLD, fontWeight: 900, fontSize: 18 }}>{tr(lang, COPY.accessTitle)}</div>
            <p style={{ color: TEXT, margin: "7px 0 12px", lineHeight: 1.55 }}>{simulatedAccess?.message || (lang === "pl" ? "Hood poznał już Twój styl. PRO odblokowuje głębszy, ciągły trening dopasowany do Ciebie." : lang === "es" ? "Hood ya conoce tu estilo. PRO desbloquea un entrenamiento continuo y mucho más personalizado." : "Hood now knows your style. PRO unlocks deeper, continuous training built around you.")}</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 8, marginBottom: 13 }}>
              {(lang === "pl"
                ? ["Spersonalizowane puzzle", "Trening otwarć", "Partie 10-minutowe", "Analiza Twoich partii"]
                : lang === "es"
                  ? ["Puzzles personalizados", "Práctica de aperturas", "Partidas de 10 minutos", "Análisis de tus partidas"]
                  : ["Personalised puzzles", "Opening practice", "10-minute games", "Game analysis"]
              ).map((item) => <div key={item} style={{ background: "rgba(255,255,255,.055)", border: "1px solid rgba(255,255,255,.10)", borderRadius: 11, padding: "9px 10px", fontWeight: 750, fontSize: 13 }}>✓ {item}</div>)}
            </div>
            {simulatedAccess?.nextAvailableAt && <div style={{ color: MUTED, fontSize: 13, marginBottom: 12 }}>{new Date(simulatedAccess.nextAvailableAt).toLocaleString()}</div>}
            {betaMode && qa.active
              ? <Link to="/owner-qa" style={{ display: "inline-block", background: GOLD, color: "#111827", borderRadius: 11, padding: "10px 15px", textDecoration: "none", fontWeight: 900 }}>Change QA access</Link>
              : <Link to="/checkout" style={{ display: "inline-block", background: GOLD, color: "#111827", borderRadius: 11, padding: "11px 16px", textDecoration: "none", fontWeight: 900 }}>{tr(lang, COPY.upgrade)}</Link>}
          </section>
        )}

        {!simplifiedEarlyExperience && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 10, margin: "14px 0" }}>
          <InfoCard label={tr(lang, COPY.rating)} value={primaryRating || "—"} note={lichessUser ? `@${lichessUser}` : "Lichess"} />
          <InfoCard label={tr(lang, COPY.plan)} value={planTitle} note={weakest ? `${weakest.name} · ${weakest.scoreRate}%` : tr(lang, COPY.freeGame)} />
          <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 13 }}>
            <div style={miniLabel}>{tr(lang, COPY.engine).toUpperCase()}</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 7 }}>
              <button onClick={() => changeEngineLevel(-1)} style={smallButton}>−</button>
              <div style={{ textAlign: "center" }}><div style={{ fontWeight: 900, fontSize: 20 }}>{engineLevel.elo}</div><div style={{ color: MUTED, fontSize: 12 }}>{engineLevel[lang] || engineLevel.en}</div></div>
              <button onClick={() => changeEngineLevel(1)} style={smallButton}>+</button>
            </div>
          </div>
        </div>}

        {simplifiedEarlyExperience && <div style={{ margin: "12px 0 14px", color: MUTED, fontSize: 13, textAlign: "center" }}>
          {lang === "pl" ? "Wczesny etap: Hood pokazuje tylko najważniejsze rzeczy i prowadzi krok po kroku." : lang === "es" ? "Etapa inicial: Hood muestra solo lo esencial y guía paso a paso." : "Early stage: Hood shows only the essentials and guides you step by step."}
        </div>}

        {!simplifiedEarlyExperience && (lichessAllowed ? <section style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 18, padding: 13, marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 9 }}>
            <div style={{ fontWeight: 900 }}><FontAwesomeIcon icon={faLink} style={{ color: GOLD, marginRight: 7 }} />{tr(lang, COPY.connect)}</div>
            {lichessUser && <span style={{ color: "#8CE99A", fontWeight: 800, fontSize: 12 }}>@{lichessUser} · {tr(lang, COPY.connected)}</span>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input value={lichessInput} onChange={(event) => setLichessInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") connectLichess(); }} placeholder={tr(lang, COPY.username)} style={inputStyle} />
            <button onClick={connectLichess} disabled={lichessBusy} style={goldButton}>{lichessBusy ? "…" : tr(lang, COPY.connectButton)}</button>
          </div>
          {lichessUser && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8, marginTop: 11 }}>
              <MiniStat label="Rapid" value={lichessRatings.rapid || "—"} />
              <MiniStat label="Blitz" value={lichessRatings.blitz || "—"} />
              <MiniStat label={tr(lang, COPY.recent10)} value={recent ? `${recent.wins}W · ${recent.draws}D · ${recent.losses}L` : "—"} />
              <MiniStat label={tr(lang, COPY.weakest)} value={weakest?.name || "—"} />
              <MiniStat label={tr(lang, COPY.tournament)} value={recentTournament ? `${recentTournament.name}${recentTournament.rank ? ` · #${recentTournament.rank}` : ""}` : tr(lang, COPY.noTournament)} />
            </div>
          )}
        </section> : <p style={{ color: MUTED, padding: "12px 0" }}>
          {lang === "pl" ? "Ta sesja obejmuje Hood AI. Połączenie Lichess jest w sesji za £2.99." : lang === "es" ? "Esta sesión incluye Hood AI. La conexión Lichess está en la sesión de £2.99." : "This session includes Hood AI. Lichess connection is included in the £2.99 session."}
          {" "}<Link to="/training" style={{ color: GOLD }}>{lang === "pl" ? "Zobacz treningi" : lang === "es" ? "Ver sesiones" : "View sessions"}</Link>
        </p>)}

        <section className="hood-ai-board-card">
          <HoodEngineBoardWeb
            ref={boardRef}
            boardSize={650}
            onReady={handleReady}
            onCoachHint={handleBoard}
            onPracticeGuide={handlePracticeGuide}
            onPracticeWrongCandidate={handleWrongCandidate}
            onSideChange={setPlayerColor}
            onEngineError={(message) => setError(String(message || "Stockfish error"))}
          />
          <div className="hood-ai-board-controls" style={{ display: "flex", justifyContent: "center", gap: 7, flexWrap: "wrap", marginTop: 3 }}>
            <button onClick={() => boardRef.current?.undoMove?.()} style={controlButton}><FontAwesomeIcon icon={faArrowLeft} /> Undo</button>
            <button onClick={() => boardRef.current?.redoMove?.()} style={controlButton}><FontAwesomeIcon icon={faArrowRight} /> Redo</button>
            <button onClick={startFreeGame} style={controlButton}><FontAwesomeIcon icon={faRotateLeft} /> Restart</button>
            <button onClick={() => runAction("OPENING_DRILL")} disabled={!weakest} style={{ ...controlButton, opacity: weakest ? 1 : .5 }}><FontAwesomeIcon icon={faBolt} /> {tr(lang, COPY.drill)}</button>
            <button onClick={() => runAction("PUZZLE")} style={controlButton}>{tr(lang, COPY.puzzle)}</button>
            <button onClick={() => messages.length && speak(messages[messages.length - 1]?.text)} disabled={!voiceOn} style={{ ...controlButton, opacity: voiceOn ? 1 : .45 }}><FontAwesomeIcon icon={faVolumeHigh} /> Read again</button>
          </div>
          <div className="hood-ai-board-status" style={{ minHeight: 21, textAlign: "center", color: MUTED, fontSize: 12, paddingTop: 7 }}>
            {engineThinking ? "Stockfish…" : hoodThinking ? tr(lang, COPY.thinking) : opening?.name || ""}
          </div>
        </section>

        <section style={{ marginTop: 14, background: CARD, border: `1px solid ${BORDER}`, borderRadius: 20, overflow: "hidden" }}>
          <div style={{ maxHeight: 360, overflowY: "auto", padding: "15px 13px", display: "flex", flexDirection: "column", gap: 10 }}>
            {!messages.length && <div style={{ color: MUTED, lineHeight: 1.55 }}>{lang === "pl" ? "Zagraj pierwszy ruch albo zapytaj Hooda o trening." : lang === "es" ? "Juega la primera jugada o pregunta a Hood sobre tu entrenamiento." : "Play the first move or ask Hood what you should train."}</div>}
            {messages.map((message) => (
              <div key={message.id} style={{ alignSelf: message.role === "user" ? "flex-end" : "flex-start", maxWidth: "88%" }}>
                <div style={{ color: message.role === "user" ? "#cfe0ff" : GOLD, fontSize: 11, fontWeight: 900, marginBottom: 4 }}>{message.role === "user" ? tr(lang, COPY.you) : tr(lang, COPY.hood)}</div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => voiceOn && speakWebHoodVoice(message.text, lang)}
                  onKeyDown={(event) => {
                    if (!voiceOn) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      speakWebHoodVoice(message.text, lang);
                    }
                  }}
                  title={voiceOn ? "Tap to read this message aloud" : "Turn voice on to read aloud"}
                  style={{
                    background: message.role === "user" ? "#13284a" : "rgba(255,212,0,.075)",
                    border: `1px solid ${message.role === "user" ? "rgba(150,190,255,.18)" : BORDER}`,
                    borderRadius: 15,
                    padding: "10px 12px",
                    lineHeight: 1.55,
                    cursor: voiceOn ? "pointer" : "default",
                    WebkitTapHighlightColor: "rgba(125,211,252,.14)",
                  }}
                >
                  {message.text}
                </div>
              </div>
            ))}
            {hoodThinking && <div style={{ color: MUTED, fontSize: 13 }}>{tr(lang, COPY.thinking)}</div>}
            <div ref={chatEndRef} />
          </div>
          <div style={{ display: "flex", gap: 8, padding: 11, borderTop: "1px solid rgba(255,255,255,.08)" }}>
            <button onClick={toggleMic} style={{ ...sendButton, background: listening ? "#e85d68" : "#13284a", color: TEXT }} title={listening ? tr(lang, COPY.listening) : "Microphone"}><FontAwesomeIcon icon={faMicrophone} /></button>
            <textarea value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); sendQuestion(); } }} placeholder={listening ? tr(lang, COPY.listening) : tr(lang, COPY.ask)} rows={2} style={{ ...inputStyle, resize: "none", flex: 1 }} />
            <button onClick={() => sendQuestion()} disabled={!input.trim() || hoodThinking} style={{ ...sendButton, opacity: !input.trim() || hoodThinking ? .5 : 1 }}><FontAwesomeIcon icon={faPaperPlane} /></button>
          </div>
        </section>

        {error && <div style={{ marginTop: 13, border: "1px solid rgba(255,100,100,.28)", background: "rgba(255,90,90,.09)", color: "#ffd7d7", borderRadius: 13, padding: "10px 12px" }}>{error}</div>}
      </div>
    </div>
  );
}

function InfoCard({ label, value, note }) {
  return <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 13 }}><div style={miniLabel}>{label}</div><div style={{ fontSize: 21, fontWeight: 900, marginTop: 6, lineHeight: 1.2 }}>{value}</div><div style={{ color: MUTED, fontSize: 12, marginTop: 5 }}>{note}</div></div>;
}

function MiniStat({ label, value }) {
  return <div style={{ borderTop: "1px solid rgba(255,255,255,.08)", paddingTop: 8 }}><div style={{ color: MUTED, fontSize: 10, fontWeight: 900, letterSpacing: ".07em" }}>{label.toUpperCase()}</div><div style={{ fontSize: 13, fontWeight: 800, marginTop: 4, lineHeight: 1.35 }}>{value}</div></div>;
}

const miniLabel = { color: MUTED, fontSize: 10, fontWeight: 900, letterSpacing: ".1em" };
const inputStyle = { minWidth: 0, flex: 1, background: "#0b1830", color: TEXT, border: "1px solid rgba(255,255,255,.13)", borderRadius: 12, padding: "10px 11px", outline: "none" };
const goldButton = { border: 0, background: GOLD, color: "#111827", borderRadius: 12, padding: "0 14px", fontWeight: 900, cursor: "pointer" };
const smallButton = { border: `1px solid ${BORDER}`, background: "#0b1830", color: TEXT, borderRadius: 9, minWidth: 36, height: 36, fontWeight: 900, cursor: "pointer" };
const controlButton = { border: "1px solid rgba(255,255,255,.13)", background: "#0b1830", color: TEXT, borderRadius: 10, padding: "8px 10px", fontWeight: 800, cursor: "pointer" };
const sendButton = { width: 44, minWidth: 44, height: 44, border: 0, borderRadius: 12, background: GOLD, color: "#111827", fontWeight: 900, cursor: "pointer" };

